import { useParams } from 'react-router-dom'
import { sortBy } from 'lodash-es'
import { useQueries } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useStructuralPlanningStore, useElementLoadStore, useStiffeningStore } from '@editorStores'
import {
  getElementLoads,
  getStiffeningIntervals,
  getStiffeningProposal,
  getSlabStiffening,
  getHorizontalDistributionSettings,
} from '@queries'

const useStructuralPlanningQueries = () => {
  const setProposal = useStructuralPlanningStore(state => state.setProposal)
  const setHorizontalDistributionSettings = useStructuralPlanningStore(
    state => state.setHorizontalDistributionSettings,
  )
  const setUpperMostCeilingNotStiffening = useStructuralPlanningStore(
    state => state.setUpperMostCeilingNotStiffening,
  )
  const setStiffeningIntervals = useStiffeningStore(state => state.setStiffeningIntervals)

  const setLoadsPerElements = useElementLoadStore(state => state.setLoadsPerElements)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()

  return useQueries({
    queries: [
      {
        queryKey: getStiffeningProposal.getKey(projectId),
        queryFn: () => getStiffeningProposal.request(projectId),
        onSuccess: setProposal,
        onError: () => enqueueSnackbar('Fehler beim Laden des Vorschlags für die Aussteiffung.'),
      },
      {
        queryKey: getHorizontalDistributionSettings.getKey(projectId),
        queryFn: () => getHorizontalDistributionSettings.request(projectId),
        onSuccess: setHorizontalDistributionSettings,
        onError: () => enqueueSnackbar('Fehler beim Laden der Verteilungseinstellungen'),
      },
      {
        queryKey: getStiffeningIntervals.getKey(projectId),
        queryFn: () => getStiffeningIntervals.request(projectId),
        onSuccess: setStiffeningIntervals,
        onError: () => enqueueSnackbar('Fehler beim Laden der aussteiffenden Segmente.'),
      },
      {
        queryKey: getElementLoads.getKey(projectId),
        queryFn: () => getElementLoads.request(projectId),
        onSuccess: setLoadsPerElements,
        onError: () => enqueueSnackbar('Fehler beim Laden der definierten Lasten.'),
      },
      {
        queryKey: getSlabStiffening.getKey(projectId),
        queryFn: () => getSlabStiffening.request(projectId),
        onSuccess: (data: SlabStiffening[]) => {
          // the request returns a list but for now we will only have one element
          // posted and returned
          // sort ascending
          const sortedData = sortBy(data, 'storey')
          setUpperMostCeilingNotStiffening(
            sortedData[sortedData.length - 1]?.stiffening === 'NotStiffening',
          )
        },
        onError: () =>
          enqueueSnackbar('Fehler beim Laden der Aussteifungskonfiguration für Decken'),
      },
    ],
  })
}

export default useStructuralPlanningQueries
